const namespace = 'GLOBAL_ASK_AICA_';

export const GLOBAL_ASK_AICA_CONSTANTS = {
    ADD_MESSAGE: `${namespace}ADD_MESSAGE`,
    UPDATE_MESSAGE: `${namespace}UPDATE_MESSAGE`,
    UPDATE_MESSAGE_METADATA: `${namespace}UPDATE_MESSAGE_METADATA`,
    REMOVE_MESSAGES: `${namespace}REMOVE_MESSAGES`,
    INITIALISE_MESSAGE_THREAD: `${namespace}INITIALISE_MESSAGE_THREAD`,
    SET_IS_LOADING: `${namespace}SET_IS_LOADING`,
    SET_IS_LOADING_CHAT_HISTORY: `${namespace}SET_IS_LOADING_CHAT_HISTORY`,
    SET_ERROR: `${namespace}SET_ERROR`,
    SET_REASONING: `${namespace}SET_REASONING`,
    RESET_REASONING: `${namespace}RESET_REASONING`,

    // Gate-transition constants. NOTE: `decline`/`companyLess` are set as part of
    // SET_GATE_OUTCOME (see reducer.ts::applyGateOutcome) — there is deliberately
    // no standalone SET_DECLINE/SET_COMPANY_LESS action (removed as dead code,
    // human review #15: defined but never dispatched anywhere).
    SET_GATE_OUTCOME: `${namespace}SET_GATE_OUTCOME`,
    SET_CURRENT_COMPANY: `${namespace}SET_CURRENT_COMPANY`,
    SET_ASK_BACK: `${namespace}SET_ASK_BACK`,

    SET_CONVERSATION_ID: `${namespace}SET_CONVERSATION_ID`,
    SWITCH_CONVERSATION: `${namespace}SWITCH_CONVERSATION`,
    RESET_GLOBAL_CHAT: `${namespace}RESET_GLOBAL_CHAT`,
    RESET_GATE_STATE: `${namespace}RESET_GATE_STATE`,

    SET_CONVERSATIONS: `${namespace}SET_CONVERSATIONS`,
    SET_LOADING_CONVERSATIONS: `${namespace}SET_LOADING_CONVERSATIONS`,

    SET_IS_CLEARING_CHAT: `${namespace}SET_IS_CLEARING_CHAT`,
    SET_HAS_MORE_DATA: `${namespace}SET_HAS_MORE_DATA`,

    SET_PENDING_NEW_CONVERSATION: `${namespace}SET_PENDING_NEW_CONVERSATION`,
};

/** Athena endpoints — relative to athenaBaseUrl (which already ends in "/athena"). */
export const GLOBAL_ASK_AICA_ENDPOINTS = {
    CHAT: '/chat',
    CONVERSATIONS_USER: '/chat/conversations/user',
    CONVERSATION_HISTORY: '/chat/conversation/history',
};

export const DEFAULT_GLOBAL_SOURCES = ['searchTheWeb', 'trustedPublicRecords', 'consentData', 'dataRoom'];

/** Explicit page_size for restoring the user's single portfolio conversation's
 * history (F-9/F-D). Athena validates `page_size` as `Query(5, ge=1, le=50)` —
 * 200 was above the BE's ceiling and made every resume-on-open request 422,
 * rendering a blank panel (RECUR-27369 R-1). 50 is the largest page Athena
 * will accept. There is no "load more" UI wired on the portfolio surface yet
 * (see hasMoreData / MessagesContainer's `fetchChatHistory` no-op there) — a
 * conversation longer than 50 pairs (100 messages) stays truncated until that
 * follow-up ships; hasMoreData at least surfaces that a page was full. */
export const GLOBAL_HISTORY_PAGE_SIZE = 50;

/** Prefix of Athena's raw ask_back(kind:"pick") prompt text ("I found multiple
 * companies matching 'X'. Which one did you mean? 1. … 2. … 3. …"). The docked
 * CompanyPicker is the whole response for that turn — this prompt must never
 * render as a chat bubble. Live turns are fixed at the source (see
 * useGlobalAskAica.runTurn, which never persists it as message content); this
 * prefix is a content-match guard for conversations reloaded from backend
 * history, which carries no per-turn gate_outcome to key off structurally. */
export const ASK_BACK_PROMPT_PREFIX = 'I found multiple companies matching';

/**
 * Figma is the source of truth for the keys that have a node — do not reword those.
 *
 * ⚠️ The RECUR-27889 block at the bottom has NO Figma node: the multi-slot picker
 * form and the answer-scope line introduced six net-new elements the design file
 * does not cover (Confirm, the locked `resolved` row, the marked `not_in_book` row,
 * the composer hint, the scope line, the answer chips). RECUR-27889 grants explicit
 * permission to author them in the existing idiom and take a design-review pass
 * afterwards — so this copy IS reword-able, unlike everything above it, until
 * design lands nodes for it.
 */
export const GLOBAL_ASK_AICA_COPY = {
    CHIP_PREFIX: 'Answering for',
    PICKER_TITLE: 'Select correct company here',
    ENTRY_LABEL: 'Ask AICA',
    /** Fallback when Athena resolves a company without a display name (F-15) —
     * mirrors the leads ConversationSidebar's "Unknown user" fallback pattern. */
    UNKNOWN_COMPANY: 'Unknown company',

    // ── RECUR-27889, no Figma node (see the block comment above) ──────────────

    /** The picker's single submit. Row taps no longer submit — this does, once
     * every ambiguous slot is answered, and it submits them all atomically. */
    PICKER_CONFIRM: 'Confirm',
    ANSWER_SCOPE_CHIP_TITLE: (name: string) => `Ask follow-ups about ${name}`,
    /** Sits on a `resolved` slot's locked row. The point of showing a row the user
     * cannot change is that a WRONG auto-match becomes visible instead of silent. */
    PICKER_RESOLVED_HINT: 'Matched automatically',
    /** A `not_in_book` slot. There is deliberately no inline input — RECUR-27369
     * removed the free-text row and its absence is asserted by test; refinement is
     * a normal composer message, so the hint points there. */
    PICKER_NOT_IN_BOOK_HINT: 'Not in your portfolio — retype it in the message box below',
    /** A `search_failed` slot. Distinct from the above on purpose, and the backend
     * distinguishes them for the same reason: during a search outage a company that
     * genuinely IS in the user's book must not be told it is not in their portfolio.
     * The remedy differs too — retyping cannot fix an outage, so this points at
     * retrying rather than at the composer. */
    PICKER_SEARCH_FAILED_HINT: "Couldn't search for this just now — try again",

    /** Leads the answer-scope line, followed by the covered companies as chips. */
    ANSWER_SCOPE_PREFIX: 'Answering about:',
};

/** Per-reason copy for a name the answer could NOT cover (v4 §7 — `reason` is a
 * closed set on the wire and the frontend owns the wording). Unknown reasons fall
 * back to the generic sentence rather than rendering a raw enum at the user. */
export const ANSWER_SCOPE_EXCLUDED_COPY: Record<string, (written: string) => string> = {
    not_in_book: (written) => `${written} isn't in your portfolio, so it's excluded.`,
    // Says nothing about whether the name is in the book — during an outage it may
    // well be, and claiming otherwise would be false.
    search_failed: (written) => `We couldn't search for ${written} just now, so it's excluded.`,
    // Arises only on a `continue`, where an ambiguous carried name is DROPPED rather
    // than raising a picker. Reporting it as not_in_book would be a lie about a name
    // we found several matches for — the honest statement is that we couldn't tell
    // which one was meant. NOT YET RATIFIED in the frozen contract (flagged to the
    // architecture session 2026-08-23); if the wire value lands under another name
    // this key goes unused and answerScopeExcludedCopy's generic fallback covers it.
    ambiguous: (written) => `We couldn't tell which "${written}" you meant, so it's excluded.`,
};

export const answerScopeExcludedCopy = (written: string, reason: string): string => {
    // `reason` arrives from the wire, and a plain object literal inherits
    // Object.prototype — so "toString"/"constructor"/"valueOf" would return the
    // inherited FUNCTION, dodge the ?? fallback, and render garbage like
    // "[object Undefined]" in place of an exclusion sentence. Own keys only.
    const copy = Object.prototype.hasOwnProperty.call(ANSWER_SCOPE_EXCLUDED_COPY, reason) ? ANSWER_SCOPE_EXCLUDED_COPY[reason] : undefined;
    return (copy ?? ((name: string) => `${name} couldn't be included, so it's excluded.`))(written);
};

/** Background-buffer key for a chat that has no server conversation id yet (New Chat
 * whose first turn is still in flight). Replaced by the real id once it arrives. */
export const DRAFT_CONVERSATION_KEY = '__draft__';

/** Resume-on-open re-check delay (RECUR-28136 regression 2): a turn started an instant
 * before the resume-on-open history read landed has no assistant row yet, so the newest
 * pair is a lone user message and reads as "not in progress" — see
 * useGlobalAskAica.ts::loadConversation's one-shot retry. */
export const RESUME_HISTORY_RETRY_DELAY_MS = 2000;
